import { signOut } from 'aws-amplify/auth';
import { useAuthStore } from '@/stores/auth-store';

/** Paths that use invite/magic-link auth — never bounce these to Cognito login */
const PUBLIC_PATH_PREFIXES = ['/survey', '/wellness-center', '/wellness', '/login'];

let isHandlingAuthFailure = false;

export function isPublicAuthPath(pathname: string = window.location.pathname): boolean {
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

function clearDanglingSession(): void {
  useAuthStore.getState().clearLocalSession();
  void signOut().catch((error) => {
    console.warn('[AuthRedirect] Amplify signOut failed during auth recovery:', error);
  });
}

/**
 * Session is dead: clear dangling client state and (on staff routes) send the user to login.
 * Idempotent — safe to call from Hub, axios, and visibility handlers at once.
 */
export const handleAuthFailure = (reason: string = 'Authentication failed') => {
  if (typeof window === 'undefined') return;
  if (isHandlingAuthFailure) return;

  isHandlingAuthFailure = true;
  console.warn(`[AuthRedirect] ${reason}`);

  // Always drop zombie Cognito/Zustand state, including on public routes
  clearDanglingSession();

  // Invite/magic-link flows keep their own UX — don't send them to Cognito login
  if (isPublicAuthPath()) {
    isHandlingAuthFailure = false;
    return;
  }

  try {
    const currentPath = window.location.pathname + window.location.search;
    if (!currentPath.startsWith('/login')) {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }

    window.location.assign('/login');
  } catch (error) {
    console.error('[AuthRedirect] Error during auth failure handling:', error);
    window.location.assign('/login');
  }
};

/**
 * Get the stored redirect path after successful login.
 * Returns null when nothing was stored so callers can fall back to location.state.
 */
export const getRedirectPath = (): string | null => {
  const redirectPath = sessionStorage.getItem('redirectAfterLogin');
  sessionStorage.removeItem('redirectAfterLogin');
  return redirectPath;
};

/**
 * Check if user should be redirected to login
 */
export const shouldRedirectToLogin = (): boolean => {
  const authStore = useAuthStore.getState();
  return !authStore.user || !authStore.session?.tokens?.idToken;
};
